import { useState, useEffect } from 'react';
import { formatDistance } from '@/utils/formatters';
import { formatDate } from '@/utils/dates';
import { supabase } from '../lib/supabase';
import { X, ChevronDown, ChevronUp, LogIn, LogOut, User, Smartphone, Crown, Sparkles, Zap, Gauge, TrendingUp, Sun, Moon, Heart, Play, Compass, Gift, Share2 } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { useLanguage } from '@/hooks/useLanguage';
import { usePWAInstall } from '@/hooks/usePWAInstall';
import { useTasteProfile } from '@/hooks/useTasteProfile';
import type { AiMode } from '@/hooks/useSettings';
import type { WeatherData } from '@/hooks/useWeather';
import { CancelModal } from './CancelModal';
import { deleteAccount } from '@/lib/deleteAccount';
import { useUiFeedbackContext } from '@/contexts/UiFeedbackContext';
import { WeatherWidget } from './WeatherWidget';

export interface DistanceSettings {
  walking: { '1-2-hrs': number; 'half-day': number; 'full-day': number; '2-3-days': number };
  transit: { '1-2-hrs': number; 'half-day': number; 'full-day': number; '2-3-days': number };
  taxi: { '1-2-hrs': number; 'half-day': number; 'full-day': number; '2-3-days': number };
}

export interface CategorySettings {
  hungry: string[];
  'must-sees': string[];
  adventurous: string[];
  'hidden-gems': string[];
  popular: string[];
  'feeling-lucky': string[];
}

interface SettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  aiMode: AiMode;
  onAiModeChange: (mode: AiMode) => void;
  distanceSettings: DistanceSettings;
  onDistanceChange: (settings: DistanceSettings) => void;
  showOpenOnly: boolean;
  onShowOpenOnlyChange: (value: boolean) => void;
  minRating: number;
  onMinRatingChange: (value: number) => void;
  isAuthenticated: boolean;
  userEmail?: string | null;
  isAdmin?: boolean;
  onOpenAuth: () => void;
  onSignOut: () => void;
  subscriptionType?: 'free' | 'trip_pass' | 'monthly' | 'yearly';
  onUpgradeToPremium?: () => void;
  onCancelPremium?: () => void;
  onPauseSubscription?: (months: 1 | 2 | 3 | 'indefinite') => Promise<void>;
  subscriptionExpiresAt?: string | null;
  onResumeSubscription?: () => Promise<void>;
  subscriptionPausedUntil?: string | null;
  themeMode?: 'light' | 'dark' | 'system';
  onThemeModeChange?: (mode: 'light' | 'dark' | 'system') => void;
  asPage?: boolean;
  pageMode?: 'settings' | 'filters';
  userId?: string;
  referralCode?: string | null;
  tasteLikeCount?: number;
  tasteDislikeCount?: number;
  isPremium?: boolean;
  savedInterests?: Record<string, string[]>;
  onAddInterest?: (vibe: string, interest: string) => Promise<boolean>;
  onRemoveInterest?: (vibe: string, interest: string) => void;
  onOpenPro?: () => void;
  deepSearchesRemaining?: number;
  weather?: WeatherData | null;
  weatherLoading?: boolean;
  considerWeather?: boolean;
  onConsiderWeatherChange?: (value: boolean) => void;
}

const DISTANCE_RANGES = {
  walking: {
    '1-2-hrs': [1000, 1500, 2500, 3500, 5000],
    'half-day': [2000, 3500, 5000, 7500, 10000],
    'full-day': [4000, 7000, 10000, 15000, 20000],
    '2-3-days': [5000, 10000, 15000, 25000, 35000]
  },
  transit: {
    '1-2-hrs': [5000, 10000, 15000, 25000, 35000],
    'half-day': [10000, 20000, 35000, 45000, 50000],
    'full-day': [15000, 30000, 45000, 50000, 50000],
    '2-3-days': [20000, 35000, 50000, 50000, 50000]
  },
  taxi: {
    '1-2-hrs': [10000, 20000, 40000, 50000, 50000],
    'half-day': [25000, 40000, 50000, 50000, 50000],
    'full-day': [35000, 50000, 50000, 50000, 50000],
    '2-3-days': [50000, 50000, 50000, 50000, 50000]
  }
};

function getSliderStep(transport: keyof typeof DISTANCE_RANGES, time: keyof typeof DISTANCE_RANGES['walking'], value: number): number {
  const range = DISTANCE_RANGES[transport][time];
  const index = range.findIndex(v => v === value);
  return index >= 0 ? index : 2;
}

export function SettingsModal({
  isOpen,
  onClose,
  aiMode,
  onAiModeChange,
  distanceSettings,
  onDistanceChange,
  showOpenOnly,
  onShowOpenOnlyChange,
  minRating,
  onMinRatingChange,
  isAuthenticated,
  userEmail,
  isAdmin,
  onOpenAuth,
  onSignOut,
  subscriptionType = 'free',
  onUpgradeToPremium,
  onCancelPremium,
  onPauseSubscription,
  onResumeSubscription,
  subscriptionPausedUntil,
  subscriptionExpiresAt,
  themeMode = 'system',
  onThemeModeChange,
  asPage = false,
  pageMode = 'settings',
  userId,
  referralCode,
  tasteLikeCount = 0,
  tasteDislikeCount = 0,
  isPremium = false,
  savedInterests = {},
  onAddInterest,
  onRemoveInterest,
  onOpenPro,
  deepSearchesRemaining,
  weather,
  weatherLoading,
  considerWeather,
  onConsiderWeatherChange
}: SettingsModalProps) {
  const { t } = useLanguage();
  const { profile: tasteProfile } = useTasteProfile(userId ?? null);
  const { promptInstall, isInstallable, isInstalled, platform } = usePWAInstall();
  const [expandedTransport, setExpandedTransport] = useState<string | null>('walking');
  const [newInterestText, setNewInterestText] = useState('');
  const [expandedVibe, setExpandedVibe] = useState<string | null>('hungry');
  const [activeSlider, setActiveSlider] = useState<string | null>(null);
  const [installing, setInstalling] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [shareCopiedToast, setShareCopiedToast] = useState(false);
  const [deleteStage, setDeleteStage] = useState<'idle' | 'confirm' | 'deleting'>('idle');
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const feedback = useUiFeedbackContext();

  const handleDeleteAccount = async () => {
    setDeleteStage('deleting');
    setDeleteError(null);
    const result = await deleteAccount();
    if (result === 'deleted') {
      setDeleteStage('idle');
      onClose();
      feedback.showCreditToast(t.settings.deleteAccountDone, 6000);
      return;
    }
    setDeleteStage('confirm');
    setDeleteError(result === 'active_subscription' ? t.settings.deleteAccountActiveSub
      : result === 'admin_account' ? t.settings.deleteAccountAdmin
      : t.settings.deleteAccountFailed);
  };

  // H-216: referral events log (Recent joiners under "Share with a friend").
  interface ReferralEventRow {
    id: string;
    referee_email_concealed: string;
    referee_country: string | null;
    interest_bonus_awarded: number;
    deep_bonus_awarded: number;
    created_at: string;
  }
  const [referralEvents, setReferralEvents] = useState<ReferralEventRow[]>([]);
  const [referralTotals, setReferralTotals] = useState<{ count: number; interest: number; deep: number }>({ count: 0, interest: 0, deep: 0 });
  const [referralLogExpanded, setReferralLogExpanded] = useState(false);
  useEffect(() => {
    if (!isOpen || !isAuthenticated || !userId || !supabase) return;
    let cancelled = false;
    const sb = supabase;
    (async () => {
      const { data, error } = await sb
        .from('referral_events')
        .select('id, referee_email_concealed, referee_country, interest_bonus_awarded, deep_bonus_awarded, created_at')
        .eq('referrer_id', userId)
        .order('created_at', { ascending: false })
        .limit(10);
      if (cancelled) return;
      if (!error && data) {
        setReferralEvents(data as ReferralEventRow[]);
        const totals = data.reduce(
          (acc, r) => ({
            count: acc.count + 1,
            interest: acc.interest + (r.interest_bonus_awarded ?? 0),
            deep: acc.deep + (r.deep_bonus_awarded ?? 0),
          }),
          { count: 0, interest: 0, deep: 0 },
        );
        setReferralTotals(totals);
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, isAuthenticated, userId]);
  const formatRelativeTime = (iso: string) => {
    try {
      const ms = Date.now() - new Date(iso).getTime();
      const days = Math.floor(ms / 86_400_000);
      if (days < 1) return 'today';
      if (days === 1) return '1 day ago';
      if (days < 30) return `${days} days ago`;
      const months = Math.floor(days / 30);
      return months === 1 ? '1 month ago' : `${months} months ago`;
    } catch { return ''; }
  };

  const isPaused = subscriptionPausedUntil && new Date(subscriptionPausedUntil) > new Date();

  if (!isOpen) return null;

  const handleInstall = async () => {
    setInstalling(true);
    await promptInstall();
    setInstalling(false);
  };

  const handleDistanceSlider = (
    transport: keyof DistanceSettings,
    time: keyof DistanceSettings['walking'],
    step: number
  ) => {
    const newValue = DISTANCE_RANGES[transport][time][step];
    onDistanceChange({
      ...distanceSettings,
      [transport]: {
        ...distanceSettings[transport],
        [time]: newValue
      }
    });
  };

  return (
    <div className={asPage ? 'min-h-screen bg-[var(--bg-primary)]' : 'fixed inset-0 z-50 flex items-center justify-center p-4'}>
      {!asPage && (
        <div
          role="presentation"
          className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          onClick={onClose}
        />
      )}

      <div
        role={asPage ? undefined : 'dialog'}
        aria-labelledby={asPage ? undefined : 'settings-modal-title'}
        className={asPage
        ? 'w-full max-w-2xl mx-auto'
        : 'relative w-full max-w-lg max-h-[85vh] overflow-hidden rounded-2xl bg-[var(--bg-primary)] border border-[var(--border)] shadow-2xl animate-fade-in'
      }>
        <div className={`sticky top-0 z-10 flex items-center justify-between p-4 border-b border-[var(--border)] ${asPage ? 'bg-[var(--bg-primary)]/95 backdrop-blur-sm' : 'bg-[var(--bg-primary)]'}`}>
          <div className="flex items-center gap-2">
            <p id="settings-modal-title" className={`font-semibold tracking-tight text-[var(--text-primary)] ${asPage ? 'text-xl' : 'text-lg'}`}>{asPage && pageMode === 'filters' ? (t.settings.filters || 'Filters') : t.settings.title}</p>
            {isAuthenticated && subscriptionType !== 'free' && (
              <div className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-gradient-to-r from-[var(--accent-gold)]/20 to-amber-600/10 border border-[var(--accent-gold)]/30">
                <Crown className="w-3 h-3 text-[var(--accent-gold)]" />
                <span className="text-[11px] font-semibold text-[var(--accent-gold)]">Pro</span>
              </div>
            )}
          </div>
          {!asPage && (
            <button
              onClick={onClose}
              aria-label="Close settings"
              className="p-2 rounded-lg hover:bg-[var(--bg-elevated)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>

        <div className={asPage ? 'px-4 py-6 pb-24 space-y-6' : 'overflow-y-auto max-h-[calc(85vh-64px)] p-4 space-y-6'}>
          {(!asPage || pageMode === 'settings') && <>
          <section>
            <div className="p-3 rounded-lg bg-[var(--bg-elevated)] border border-[var(--border)]">
              {isAuthenticated ? (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-[var(--accent-teal)]/20 flex items-center justify-center">
                      <User className="w-4 h-4 text-[var(--accent-teal)]" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-[var(--text-primary)]">{t.common.signedIn}</p>
                      <p className="text-xs text-[var(--text-secondary)] truncate max-w-[180px]">{userEmail}</p>
                    </div>
                  </div>
                  <button
                    onClick={onSignOut}
                    aria-label="Sign out"
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-[var(--status-red)] hover:bg-[var(--status-red-bg)] transition-colors"
                  >
                    <LogOut className="w-3.5 h-3.5" />
                    {t.common.signOut}
                  </button>
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-medium text-[var(--text-primary)]">{t.common.account}</h3>
                    <p className="text-xs text-[var(--text-secondary)] mt-0.5">{t.settings.signInToSave}</p>
                  </div>
                  <button
                    onClick={onOpenAuth}
                    aria-label="Sign in"
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-[var(--accent-teal)] text-white hover:bg-[var(--accent-teal)]/90 transition-colors"
                  >
                    <LogIn className="w-3.5 h-3.5" />
                    {t.common.signIn}
                  </button>
                </div>
              )}
            </div>
          </section>

          {onThemeModeChange && (
            <section>
              <div className="p-3 rounded-lg bg-[var(--bg-elevated)] border border-[var(--border)]">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-[var(--text-primary)]">{t.settings.theme || 'Theme'}</span>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => onThemeModeChange(themeMode === 'light' ? 'system' : 'light')}
                      aria-label={t.settings.themeLight}
                      aria-pressed={themeMode === 'light'}
                      className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all ${
                        themeMode === 'light'
                          ? 'bg-[var(--accent-teal)]/20 ring-1 ring-[var(--accent-teal)] text-[var(--accent-teal)]'
                          : 'text-[var(--text-secondary)] hover:bg-[var(--bg-primary)] opacity-70 hover:opacity-100'
                      }`}
                    >
                      <Sun className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => onThemeModeChange(themeMode === 'dark' ? 'system' : 'dark')}
                      aria-label={t.settings.themeDark}
                      aria-pressed={themeMode === 'dark'}
                      className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all ${
                        themeMode === 'dark'
                          ? 'bg-[var(--accent-teal)]/20 ring-1 ring-[var(--accent-teal)] text-[var(--accent-teal)]'
                          : 'text-[var(--text-secondary)] hover:bg-[var(--bg-primary)] opacity-70 hover:opacity-100'
                      }`}
                    >
                      <Moon className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </section>
          )}

          <section>
            <div className="rounded-xl border border-[var(--border)] overflow-hidden">
              <div className="flex items-center justify-between p-4 bg-[var(--bg-elevated)]">
                <div className="flex items-center gap-3">
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center transition-all duration-300 ${
                    aiMode !== 'off'
                      ? 'bg-[var(--accent-teal)]/20'
                      : 'bg-[var(--bg-primary)]'
                  }`}>
                    <Sparkles className={`w-4.5 h-4.5 transition-colors duration-300 ${
                      aiMode !== 'off' ? 'text-[var(--accent-teal)]' : 'text-[var(--text-secondary)]'
                    }`} />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-[var(--text-primary)]">{t.settings.aiMode}</h3>
                    <p className="text-xs text-[var(--text-secondary)] mt-0.5">{t.settings.aiModeDescription}</p>
                  </div>
                </div>
                <button
                  onClick={() => onAiModeChange(aiMode === 'off' ? 'medium' : 'off')}
                  aria-label="Toggle AI mode"
                  aria-pressed={aiMode !== 'off'}
                  className={`relative w-11 h-6 rounded-full transition-colors ${
                    aiMode !== 'off' ? 'bg-[var(--accent-teal)]' : 'bg-[var(--bg-primary)] border border-[var(--border)]'
                  }`}
                >
                  <span
                    className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${
                      aiMode !== 'off' ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>

              {aiMode === 'off' && (
                <div className="px-4 py-3 bg-[var(--status-amber-bg)] border-t border-[var(--status-amber)]/40">
                  <p className="text-xs text-[var(--status-amber)]">
                    {t.settings.aiOffNotice}
                  </p>
                </div>
              )}

              {aiMode !== 'off' && (
                <div className="p-4 bg-[var(--bg-primary)] border-t border-[var(--border)] space-y-3">
                  <p className="text-xs text-[var(--text-secondary)]">{t.settings.aiLevelPrompt}</p>
                  <div className="grid grid-cols-3 gap-2">
                    {([
                      { mode: 'low' as AiMode, icon: Zap, color: 'var(--text-secondary)' },
                      { mode: 'medium' as AiMode, icon: Gauge, color: 'var(--accent-gold)' },
                      { mode: 'high' as AiMode, icon: TrendingUp, color: 'var(--accent-teal)' },
                    ]).map(({ mode, icon: Icon, color }) => {
                      const isSelected = aiMode === mode;
                      const baseLabel = t.settings.aiLevels[mode as 'low' | 'medium' | 'high'];
                      const label = mode === 'high' && subscriptionType === 'free' && deepSearchesRemaining !== undefined
                        ? `${baseLabel} (${deepSearchesRemaining})`
                        : baseLabel;
                      const desc = t.settings.aiLevelDescriptions[mode as 'low' | 'medium' | 'high'];
                      const isLocked = mode === 'high' && subscriptionType === 'free' && (deepSearchesRemaining === undefined || deepSearchesRemaining <= 0);

                      return (
                        <button
                          key={mode}
                          onClick={() => {
                            if (isLocked && onUpgradeToPremium) {
                              onUpgradeToPremium();
                            } else {
                              onAiModeChange(mode);
                            }
                          }}
                          aria-label={`AI level: ${baseLabel}`}
                          aria-pressed={isSelected}
                          className={`relative flex flex-col items-center gap-2 p-3 rounded-xl border transition-all duration-200 ${
                            isSelected
                              ? 'border-opacity-50 scale-[1.02] shadow-lg'
                              : 'border-[var(--border)] hover:border-opacity-30 hover:bg-[var(--bg-elevated)]/50'
                          }`}
                          style={{
                            borderColor: isSelected ? color : undefined,
                            background: isSelected ? `${color}10` : undefined,
                            boxShadow: isSelected ? `0 4px 20px ${color}15` : undefined,
                          }}
                        >
                          <div
                            className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-200 ${
                              isSelected ? 'scale-110' : ''
                            }`}
                            style={{
                              background: isSelected ? `${color}20` : 'var(--bg-elevated)',
                            }}
                          >
                            <Icon
                              className="w-4 h-4 transition-colors duration-200"
                              style={{ color: isSelected ? color : 'var(--text-secondary)' }}
                            />
                          </div>
                          <div className="text-center">
                            <p
                              className="text-xs font-semibold transition-colors duration-200"
                              style={{ color: isSelected ? color : 'var(--text-primary)' }}
                            >
                              {label}
                            </p>
                            <p className="text-[11px] text-[var(--text-secondary)] mt-0.5 leading-tight">
                              {desc}
                            </p>
                          </div>
                          {isSelected && !isLocked && (
                            <div
                              className="absolute -top-1 -right-1 w-3 h-3 rounded-full border-2 border-[var(--bg-primary)]"
                              style={{ background: color }}
                            />
                          )}
                          {isLocked && (
                            <div className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-[var(--accent-gold)] flex items-center justify-center border-2 border-[var(--bg-primary)]">
                              <Crown className="w-2.5 h-2.5 text-white" />
                            </div>
                          )}
                          {mode === 'high' && !isPremium && !isLocked && (
                            <span className="absolute -top-1.5 -right-1.5 px-1 py-0.5 rounded text-[8px] font-bold bg-[var(--accent-gold)] text-black leading-none">
                              PRO
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </section>

          {isAuthenticated && tasteProfile && tasteProfile.totalInteractions > 0 && (
            <section>
              <div className="p-4 rounded-lg bg-[var(--bg-elevated)] border border-[var(--border)] space-y-3">
                <div className="flex items-center gap-2 mb-2">
                  <TrendingUp className="w-4 h-4 text-[var(--accent-teal)]" />
                  <h3 className="text-sm font-semibold text-[var(--text-primary)]">{t.settings.tasteProfile || 'Taste Map'}</h3>
                </div>
                <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-[var(--text-secondary)]">
                  {tasteLikeCount > 0 && <span>👍 Liked: {tasteLikeCount}</span>}
                  {tasteDislikeCount > 0 && <span>👎 Disliked: {tasteDislikeCount}</span>}
                  <span>🗺️ Trips: {tasteProfile.tripsPlanned}</span>
                  {tasteProfile.placesSaved > 0 && <span>🔖 Saved: {tasteProfile.placesSaved}</span>}
                  {tasteProfile.sharesCount > 0 && <span>🔗 Shares: {tasteProfile.sharesCount}</span>}
                  {(() => {
                    const totalInterests = Object.values(savedInterests).reduce((sum, arr) => sum + arr.length, 0);
                    return totalInterests > 0 ? <span>⭐ Interests: {totalInterests}</span> : null;
                  })()}
                </div>

                {/* Combined tag cloud — categories + vibes in one flow */}
                {(tasteProfile.topCategories.length > 0 || tasteProfile.topVibes.length > 0) && (
                  <div className="flex flex-wrap gap-1.5">
                    {tasteProfile.topVibes.map((vibe) => (
                      <span key={`v-${vibe.name}`} className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-[var(--accent-gold)]/15 text-[var(--accent-gold)] border border-[var(--accent-gold)]/20">
                        {vibe.name}
                      </span>
                    ))}
                    {tasteProfile.topCategories.map((cat) => (
                      <span key={`c-${cat.name}`} className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-[var(--accent-teal)]/15 text-[var(--accent-teal)] border border-[var(--accent-teal)]/20">
                        {cat.name}
                      </span>
                    ))}
                  </div>
                )}

                {/* Travel style — compact inline */}
                {tasteProfile.distancePreference && (
                  <div className="flex items-center gap-1.5">
                    <Compass className="w-3 h-3 text-[var(--text-secondary)]" />
                    <div className="flex gap-0.5">
                      {(['near', 'medium', 'far'] as const).map((level) => (
                        <div key={level} className={`h-1.5 w-5 rounded-full ${
                          level === 'near' ? (tasteProfile.distancePreference === 'near' ? 'bg-[var(--accent-teal)]' : 'bg-[var(--border)]')
                            : level === 'medium' ? (['medium', 'far'].includes(tasteProfile.distancePreference!) ? 'bg-[var(--accent-gold)]' : 'bg-[var(--border)]')
                            : (tasteProfile.distancePreference === 'far' ? 'bg-amber-500' : 'bg-[var(--border)]')
                        }`} />
                      ))}
                    </div>
                    <span className="text-[11px] text-[var(--text-secondary)]">
                      {tasteProfile.distancePreference === 'near' ? t.settings.prefersNearby
                        : tasteProfile.distancePreference === 'far' ? t.settings.prefersExploring
                        : t.settings.mixedExplorer}
                    </span>
                  </div>
                )}

                {/* Aggregated transport & time preferences */}
                {(() => {
                  const tc = tasteProfile.transportCounts || {};
                  const totalT = Object.values(tc).reduce((a, b) => a + b, 0);
                  if (totalT === 0) return null;
                  const transportLabels: Record<string, string> = { walking: '🚶', transit: '🚇', taxi: '🚕' };
                  const sorted = Object.entries(tc).sort((a, b) => b[1] - a[1]);
                  return (
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="text-[11px] text-[var(--text-secondary)]">Transport:</span>
                      {sorted.map(([k, v]) => (
                        <span key={k} className="text-[11px] text-[var(--text-secondary)]">
                          {transportLabels[k] || k} {Math.round((v / totalT) * 100)}%
                        </span>
                      ))}
                    </div>
                  );
                })()}
                {(() => {
                  const tm = tasteProfile.timeCounts || {};
                  const totalTm = Object.values(tm).reduce((a, b) => a + b, 0);
                  if (totalTm === 0) return null;
                  const timeLabels: Record<string, string> = { '1-2-hrs': '1-2hrs', 'half-day': 'Half day', 'full-day': 'Full day', '2-3-days': '2-3 days' };
                  const sorted = Object.entries(tm).sort((a, b) => b[1] - a[1]);
                  return (
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="text-[11px] text-[var(--text-secondary)]">Time:</span>
                      {sorted.map(([k, v]) => (
                        <span key={k} className="text-[11px] text-[var(--text-secondary)]">
                          {timeLabels[k] || k} {Math.round((v / totalTm) * 100)}%
                        </span>
                      ))}
                    </div>
                  );
                })()}

                <p className="text-[10px] text-[var(--text-secondary)]/40">
                  {t.settings.tasteImproves || 'Improves with every interaction'}
                </p>
              </div>
            </section>
          )}

          </>}

          {(!asPage || pageMode === 'filters') && <>

          {(weather || weatherLoading) && (
            <section>
              <WeatherWidget
                weather={weather ?? null}
                loading={weatherLoading ?? false}
                considerWeather={considerWeather}
                onConsiderWeatherChange={onConsiderWeatherChange}
              />
            </section>
          )}

          <section>
            <div className="flex items-center justify-between p-3 rounded-lg bg-[var(--bg-elevated)] border border-[var(--border)]">
              <div>
                <h3 className="text-sm font-medium text-[var(--text-primary)]">{t.settings.showOnlyOpenPlaces}</h3>
                <p className="text-xs text-[var(--text-secondary)] mt-0.5">{t.settings.filterClosedPlaces}</p>
              </div>
              <button
                onClick={() => onShowOpenOnlyChange(!showOpenOnly)}
                aria-label="Show only open places"
                aria-pressed={showOpenOnly}
                className={`relative w-11 h-6 rounded-full transition-colors ${
                  showOpenOnly ? 'bg-[var(--accent-teal)]' : 'bg-[var(--bg-primary)] border border-[var(--border)]'
                }`}
              >
                <span
                  className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${
                    showOpenOnly ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>
          </section>

          <section>
            <div className="p-3 rounded-lg bg-[var(--bg-elevated)] border border-[var(--border)]">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="text-sm font-medium text-[var(--text-primary)]">{t.settings.minimumStarRating}</h3>
                  <p className="text-xs text-[var(--text-secondary)] mt-0.5">{t.settings.onlyShowRatingOrHigher}</p>
                </div>
                <span className="text-sm font-medium text-[var(--accent-gold)]">
                  {minRating === 0 ? t.settings.any : `${minRating.toFixed(1)}+`}
                </span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-xs text-[var(--text-secondary)]">{t.settings.any}</span>
                <input
                  type="range"
                  min={0}
                  max={5}
                  step={0.1}
                  value={minRating}
                  onChange={(e) => onMinRatingChange(parseFloat(e.target.value))}
                  className="flex-1 h-2 rounded-full appearance-none cursor-pointer accent-[var(--accent-gold)]"
                  style={{
                    background: `linear-gradient(to right, var(--accent-gold) 0%, var(--accent-gold) ${minRating * 20}%, var(--bg-primary) ${minRating * 20}%, var(--bg-primary) 100%)`
                  }}
                />
                <span className="text-xs text-[var(--text-secondary)]">5.0</span>
              </div>
            </div>
          </section>

          <section>
            <p className="text-lg font-semibold tracking-tight text-[var(--accent-gold)] mb-2">{t.settings.searchDistance}</p>
            <p className="text-xs text-[var(--text-secondary)] mb-4">
              {t.settings.adjustSearchDistance} (Max 50km)
            </p>

            <div className="space-y-2">
              {(['walking', 'transit', 'taxi'] as const).map((transport) => (
                <div key={transport} className="rounded-lg border border-[var(--border)] overflow-hidden">
                  <button
                    onClick={() => setExpandedTransport(expandedTransport === transport ? null : transport)}
                    aria-label={`${t.transport[transport]} distance settings`}
                    aria-expanded={expandedTransport === transport}
                    className="w-full flex items-center justify-between p-3 bg-[var(--bg-elevated)] hover:bg-[var(--bg-elevated)]/80 transition-colors"
                  >
                    <span className="text-sm font-medium text-[var(--text-primary)]">
                      {transport === 'walking' && '🚶 '}
                      {transport === 'transit' && '🚇 '}
                      {transport === 'taxi' && '🚕 '}
                      {t.transport[transport]}
                    </span>
                    {expandedTransport === transport ? (
                      <ChevronUp className="w-4 h-4 text-[var(--text-secondary)]" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-[var(--text-secondary)]" />
                    )}
                  </button>

                  {expandedTransport === transport && (
                    <div className="p-3 space-y-3 bg-[var(--bg-primary)]">
                      {(['1-2-hrs', 'half-day', 'full-day', '2-3-days'] as const).map((time) => {
                        const transportSettings = distanceSettings[transport];
                        if (!transportSettings) return null;
                        const currentValue = transportSettings[time];
                        const step = getSliderStep(transport, time, currentValue);
                        const sliderId = `${transport}-${time}`;
                        const isActive = activeSlider === sliderId;

                        return (
                          <div key={time} className="flex items-center gap-3">
                            <span className="text-xs text-[var(--text-secondary)] w-16 shrink-0">{t.time[time]}</span>
                            <input
                              type="range"
                              min={0}
                              max={4}
                              step={1}
                              value={step}
                              onChange={(e) => handleDistanceSlider(transport, time, parseInt(e.target.value))}
                              onMouseDown={() => setActiveSlider(sliderId)}
                              onMouseUp={() => setActiveSlider(null)}
                              onTouchStart={() => setActiveSlider(sliderId)}
                              onTouchEnd={() => setActiveSlider(null)}
                              className="flex-1 h-2 rounded-full appearance-none cursor-pointer accent-[var(--accent-teal)]"
                              style={{
                                background: `linear-gradient(to right, var(--accent-teal) 0%, var(--accent-teal) ${currentValue >= 50000 ? 100 : step * 25}%, var(--bg-elevated) ${currentValue >= 50000 ? 100 : step * 25}%, var(--bg-elevated) 100%)`
                              }}
                            />
                            <span className={`font-medium text-[var(--accent-teal)] w-16 text-right transition-all duration-150 ${
                              isActive ? 'text-base scale-110 origin-right' : 'text-xs'
                            }`}>
                              {formatDistance(currentValue)}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>

          <section>
            <div className="flex items-center justify-between mb-2">
              <p className="text-lg font-semibold tracking-tight text-[var(--accent-gold)]">Saved Interests</p>
              {!isPremium && (
                <span className="px-2 py-0.5 rounded-full bg-[var(--accent-gold)]/15 border border-[var(--accent-gold)]/30 text-[var(--accent-gold)] text-[10px] font-semibold uppercase tracking-wide">
                  Pro
                </span>
              )}
            </div>
            <p className="text-xs text-[var(--text-secondary)] mb-4">
              {isPremium
                ? 'Your interests are used to enrich searches when the Saved Interests toggle is on.'
                : 'Save interests like "vegan", "gluten free", "scuba", "hiking" and they can automatically enrich your searches. Get Pro to apply them when needed.'}
            </p>

            {(() => {
              const hasAnySavedInterests = Object.values(savedInterests).some(arr => arr && arr.length > 0);

              if (!isPremium && !hasAnySavedInterests) {
                return (
                  <div className="p-4 rounded-lg bg-[var(--bg-elevated)] border border-[var(--border)] text-center">
                    <p className="text-sm text-[var(--text-secondary)] mb-3">Save interests like "vegan", "gluten free", "scuba", "hiking" and they can automatically enrich your searches.</p>
                    {onUpgradeToPremium && (
                      <button
                        onClick={onUpgradeToPremium}
                        aria-label="Get Pro"
                        className="px-4 py-2 rounded-lg text-sm font-semibold bg-[var(--accent-gold)] text-black"
                      >
                        Get Pro
                      </button>
                    )}
                  </div>
                );
              }

              return (
                <div className="space-y-2">
                  {(['hungry', 'adventurous', 'must-sees', 'hidden-gems', 'popular', 'other'] as const).map((vibe) => {
                    const vibeInterests = savedInterests[vibe] || [];
                    const vibeEmojis: Record<string, string> = {
                      hungry: '🍜', adventurous: '🧭', 'must-sees': '📸',
                      'hidden-gems': '💎', popular: '🔥', other: '✨'
                    };
                    const vibeNames: Record<string, string> = {
                      hungry: t.vibes.hungry,
                      adventurous: t.vibes.adventurous,
                      'must-sees': t.vibes['must-sees'],
                      'hidden-gems': t.vibes['hidden-gems'],
                      popular: t.vibes.popular,
                      other: 'Other',
                    };

                    return (
                      <div key={vibe} className="rounded-lg border border-[var(--border)] overflow-hidden">
                        <button
                          onClick={() => setExpandedVibe(expandedVibe === vibe ? null : vibe)}
                          aria-label={`${vibeNames[vibe]} interests`}
                          aria-expanded={expandedVibe === vibe}
                          className="w-full flex items-center justify-between p-3 bg-[var(--bg-elevated)] hover:bg-[var(--bg-elevated)]/80 transition-colors"
                        >
                          <span className="text-sm font-medium text-[var(--text-primary)] flex items-center gap-2">
                            <span>{vibeEmojis[vibe]}</span>
                            {vibeNames[vibe]}
                          </span>
                          <div className="flex items-center gap-2">
                            {vibeInterests.length > 0 && (
                              <span className="text-xs text-[var(--accent-teal)]">{vibeInterests.length}</span>
                            )}
                            {expandedVibe === vibe ? (
                              <ChevronUp className="w-4 h-4 text-[var(--text-secondary)]" />
                            ) : (
                              <ChevronDown className="w-4 h-4 text-[var(--text-secondary)]" />
                            )}
                          </div>
                        </button>

                        {expandedVibe === vibe && (
                          <div className="p-3 bg-[var(--bg-primary)] space-y-2">
                            {vibeInterests.length > 0 && (
                              <div className="flex flex-wrap gap-2">
                                {vibeInterests.map((interest) => (
                                  <span
                                    key={interest}
                                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-[var(--accent-teal)]/25 text-[var(--accent-teal)] border border-[var(--accent-teal)]/40"
                                  >
                                    {interest}
                                    {isPremium && onRemoveInterest && (
                                      <button
                                        onClick={() => onRemoveInterest(vibe, interest)}
                                        aria-label={`Remove ${interest}`}
                                        className="ml-0.5 hover:text-[var(--status-red)] transition-colors"
                                      >
                                        <X className="w-3 h-3" />
                                      </button>
                                    )}
                                  </span>
                                ))}
                              </div>
                            )}
                            {isPremium && onAddInterest && (
                              <form
                                onSubmit={async (e) => {
                                  e.preventDefault();
                                  if (newInterestText.trim() && onAddInterest) {
                                    const added = await onAddInterest(vibe, newInterestText);
                                    if (added) setNewInterestText('');
                                  }
                                }}
                                className="flex items-center gap-2"
                              >
                                <input
                                  type="text"
                                  value={expandedVibe === vibe ? newInterestText : ''}
                                  onChange={(e) => setNewInterestText(e.target.value)}
                                  placeholder="Add interest..."
                                  maxLength={30}
                                  className="flex-1 px-3 py-1.5 rounded-lg text-xs bg-[var(--bg-elevated)] border border-[var(--border)] text-[var(--text-primary)] placeholder-[var(--text-secondary)]/50 focus:outline-none focus:border-[var(--accent-teal)]"
                                />
                                <button
                                  type="submit"
                                  disabled={!newInterestText.trim()}
                                  aria-label="Add interest"
                                  className="px-3 py-1.5 rounded-lg text-xs font-medium bg-[var(--accent-teal)] text-white disabled:opacity-30 transition-all"
                                >
                                  Add
                                </button>
                              </form>
                            )}
                            {vibeInterests.length === 0 && (
                              <p className="text-xs text-[var(--text-secondary)]/50 text-center py-2">No interests saved</p>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {!isPremium && (
                    <div className="mt-3 p-3 rounded-lg bg-[var(--accent-gold)]/5 border border-[var(--accent-gold)]/20 text-center">
                      <p className="text-xs text-[var(--text-secondary)] mb-2">Get Pro to toggle saved interests "on" for any search</p>
                      {onUpgradeToPremium && (
                        <button
                          onClick={onUpgradeToPremium}
                          aria-label="Get Pro"
                          className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-[var(--accent-gold)] text-black"
                        >
                          Get Pro
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })()}
          </section>
          </>}

          {(!asPage || pageMode === 'settings') && <>
          <section>
            <div className="p-3 rounded-lg bg-[var(--bg-elevated)] border border-[var(--border)]">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-[var(--accent-teal)]/20 flex items-center justify-center">
                    <Smartphone className="w-4 h-4 text-[var(--accent-teal)]" />
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-[var(--text-primary)]">{t.settings.installApp}</h3>
                    <p className="text-xs text-[var(--text-secondary)] mt-0.5">
                      {isInstalled
                        ? 'TripBoom is installed on this device'
                        : t.settings.installAppDescription}
                    </p>
                  </div>
                </div>
                {!isInstalled && isInstallable && (
                  <button
                    onClick={handleInstall}
                    disabled={installing}
                    aria-label="Install app"
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-[var(--accent-teal)] text-white hover:bg-[var(--accent-teal)]/90 transition-colors disabled:opacity-50"
                  >
                    {installing ? t.common.loading : t.settings.install}
                  </button>
                )}
                {!isInstalled && !isInstallable && (
                  <button
                    onClick={() => {
                      const instructions: Record<string, string> = {
                        'ios': '1. Tap the Share button \u2B06 in Safari\'s toolbar\n2. Scroll down and tap "Add to Home Screen"\n3. Tap "Add"',
                        'mac-safari': '1. In the Safari menu bar, click File\n2. Click "Add to Dock"\n\nTripBoom will appear in your Dock as a standalone app.',
                        'android-other': '1. Tap your browser\'s menu (\u22EE or \u2261)\n2. Tap "Add to Home Screen" or "Install App"\n3. Confirm',
                      };
                      alert(instructions[platform] || '1. Open your browser menu\n2. Look for "Add to Home Screen" or "Install"');
                    }}
                    aria-label="How to install"
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-[var(--accent-teal)] text-white hover:bg-[var(--accent-teal)]/90 transition-colors shrink-0"
                  >
                    How to install
                  </button>
                )}
              </div>
            </div>
          </section>

          <section>
            {(() => {
              // H-218: tap-to-share QR + "Share Now" CTA. Build the share URL once
              // and reuse for both surfaces. The native share sheet falls back to
              // a clipboard copy + toast (mirrors the existing share patterns).
              const shareUrl = (referralCode || userId)
                ? `https://tripboom.app?ref=${referralCode || userId}&utm_source=qr&utm_medium=share`
                : 'https://tripboom.app?utm_source=qr&utm_medium=share';
              const shareText = (t.share?.shareMessage || 'Try TripBoom — your AI travel concierge. Use my link or scan the QR: {url}').replace('{url}', shareUrl);
              const handleShare = async () => {
                if (navigator.share) {
                  try {
                    await navigator.share({ title: 'TripBoom', text: shareText, url: shareUrl });
                    return;
                  } catch { /* user cancelled */ }
                }
                try {
                  await navigator.clipboard.writeText(shareUrl);
                  setShareCopiedToast(true);
                  setTimeout(() => setShareCopiedToast(false), 2000);
                } catch { /* clipboard unavailable */ }
              };
              return (
                <div className="p-3 rounded-lg bg-[var(--bg-elevated)] border border-[var(--border)]">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-9 h-9 rounded-full bg-[var(--accent-teal)]/20 flex items-center justify-center">
                      <Gift className="w-4 h-4 text-[var(--accent-teal)]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-medium text-[var(--text-primary)]">{t.share?.shareWithFriendTitle || 'Share with a friend'}</h3>
                      <p className="text-xs text-[var(--text-secondary)] mt-0.5">
                        {isAuthenticated
                          ? (t.share?.earnSubtitle || 'Earn +2 Interest & +2 High AI searches when they sign up — or have your friend scan the QR.')
                          : (t.share?.scanSubtitle || 'Scan to open TripBoom on another device')}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center justify-center gap-3">
                    <button
                      type="button"
                      onClick={handleShare}
                      aria-label={t.share?.shareViaQrAria || 'Tap to share TripBoom'}
                      className="bg-white p-2 rounded-lg hover:bg-white/90 active:scale-95 transition-all flex-shrink-0"
                    >
                      <QRCodeSVG
                        value={shareUrl}
                        size={120}
                        level="M"
                        bgColor="#ffffff"
                        fgColor="#000000"
                      />
                    </button>
                    <button
                      type="button"
                      onClick={handleShare}
                      className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-[var(--accent-teal)] text-white text-sm font-semibold hover:opacity-90 active:scale-95 transition-all"
                    >
                      <Share2 className="w-4 h-4" />
                      {t.share?.shareNow || 'Share Now'}
                    </button>
                  </div>

                  {/* H-216: referrals log — totals row + expandable recent joiners. */}
                  {isAuthenticated && referralTotals.count > 0 && (
                    <div className="mt-3 pt-3 border-t border-[var(--border)]">
                      <button
                        type="button"
                        onClick={() => setReferralLogExpanded(v => !v)}
                        className="w-full flex items-center justify-between gap-2 text-left hover:opacity-90"
                        aria-expanded={referralLogExpanded}
                        aria-label={t.referral?.recentJoinersAria || 'Show recent joiners'}
                      >
                        <span className="text-xs text-[var(--text-secondary)]">
                          {(t.referral?.totalsLine || '{count} friends joined · +{interest} Interest · +{deep} Deep')
                            .replace('{count}', String(referralTotals.count))
                            .replace('{interest}', String(referralTotals.interest))
                            .replace('{deep}', String(referralTotals.deep))}
                        </span>
                        {referralLogExpanded ? (
                          <ChevronUp className="w-4 h-4 text-[var(--text-secondary)]" />
                        ) : (
                          <ChevronDown className="w-4 h-4 text-[var(--text-secondary)]" />
                        )}
                      </button>
                      {referralLogExpanded && (
                        <ul className="mt-2 space-y-1.5">
                          {referralEvents.map(ev => (
                            <li key={ev.id} className="flex items-center justify-between gap-2 text-xs">
                              <div className="flex items-center gap-2 min-w-0 flex-1">
                                <span className="text-[var(--text-secondary)] flex-shrink-0">{formatRelativeTime(ev.created_at)}</span>
                                <span className="text-[var(--text-primary)] truncate">{ev.referee_email_concealed}</span>
                                {ev.referee_country && (
                                  <span className="text-[var(--text-secondary)] flex-shrink-0">{ev.referee_country}</span>
                                )}
                              </div>
                              <span className="text-[var(--accent-teal)] flex-shrink-0">
                                +{ev.interest_bonus_awarded} / +{ev.deep_bonus_awarded}
                              </span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}
                </div>
              );
            })()}
          </section>

          {isAuthenticated && subscriptionType === 'free' && onUpgradeToPremium && (
            <section>
              <div className="p-4 rounded-lg bg-gradient-to-br from-[var(--accent-gold)]/10 to-amber-600/5 border border-[var(--accent-gold)]/30">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[var(--accent-gold)] to-amber-600 flex items-center justify-center shadow-lg shadow-amber-500/20">
                      <Crown className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-[var(--text-primary)]">
                        {t.settings.upgradeToPremium || 'Get Pro'}
                      </h3>
                      <p className="text-xs text-[var(--text-secondary)] mt-0.5">
                        {t.settings.unlimitedTripsDescription || 'Unlock unlimited features, saves & High AI mode'}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={onUpgradeToPremium}
                    aria-label={t.settings.upgrade}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold bg-gradient-to-r from-[#2dd4bf] to-[#f59e0b] text-black hover:shadow-lg hover:shadow-teal-500/25 transition-all hover:-translate-y-0.5"
                  >
                    {t.settings.upgrade || 'Upgrade'}
                  </button>
                </div>
              </div>
            </section>
          )}

          {isAuthenticated && subscriptionType !== 'free' && (
            <section>
              <div className="p-4 rounded-lg bg-gradient-to-br from-[var(--accent-gold)]/10 to-amber-600/5 border border-[var(--accent-gold)]/30">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[var(--accent-gold)] to-amber-600 flex items-center justify-center shadow-lg shadow-amber-500/20">
                      <Crown className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-[var(--accent-gold)]">
                        {isPaused
                          ? subscriptionExpiresAt && new Date(subscriptionExpiresAt) < new Date()
                            ? (t.settings.proPaused || 'Pro Paused')
                            : (t.settings.proEnabled || 'Pro Enabled')
                          : subscriptionType === 'trip_pass'
                          ? (t.settings.proEnabled || 'Pro Enabled')
                          : (t.settings.premiumMember || 'Pro Member')}
                      </h3>
                      <p className="text-xs text-[var(--text-secondary)] mt-0.5">
                        {isPaused
                          ? (() => {
                              const isIndefinite = subscriptionPausedUntil && new Date(subscriptionPausedUntil).getFullYear() >= 2099;
                              const paidExpired = subscriptionExpiresAt && new Date(subscriptionExpiresAt) < new Date();
                              const expiryDate = subscriptionExpiresAt
                                ? formatDate(subscriptionExpiresAt)
                                : null;
                              const pauseEnd = !isIndefinite && subscriptionPausedUntil
                                ? formatDate(subscriptionPausedUntil)
                                : null;
                              if (isIndefinite && paidExpired) return 'Payment frozen — resume to reactivate Pro';
                              if (isIndefinite) return expiryDate ? `Payment frozen — Pro active until ${expiryDate}` : 'Payment frozen — resume to reactivate Pro';
                              if (paidExpired && pauseEnd) return `Payment frozen — Pro expires, resumes ${pauseEnd}`;
                              if (pauseEnd && expiryDate) return `Payment frozen — Pro active until ${expiryDate}, resumes ${pauseEnd}`;
                              return 'Payment frozen — resume to reactivate Pro';
                            })()
                          : subscriptionType === 'trip_pass'
                          ? (() => {
                              if (!subscriptionExpiresAt) return t.settings.tripPassAccess || 'Trip Pass — active';
                              const diffMs = new Date(subscriptionExpiresAt).getTime() - Date.now();
                              if (diffMs <= 0) return t.settings.tripPassExpired || 'Trip Pass — expired';
                              const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
                              if (diffDays === 0) return t.settings.tripPassLastDay || 'Trip Pass — last day!';
                              return `Trip Pass — ${diffDays} day${diffDays !== 1 ? 's' : ''} left`;
                            })()
                          : subscriptionType === 'monthly'
                          ? (t.settings.monthlyAccess || 'Monthly access to unlimited features & saves')
                          : (t.settings.yearlyAccess || 'Yearly access to unlimited features & saves')}
                      </p>
                    </div>
                  </div>
                  {isPaused && onResumeSubscription ? (
                    <div className="flex items-center gap-2">
                      <button
                        onClick={onResumeSubscription}
                        aria-label="Resume subscription"
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-[var(--accent-teal)] hover:bg-[var(--accent-teal)]/10 transition-colors"
                      >
                        <Play className="w-3.5 h-3.5" />
                        Resume
                      </button>
                      {onCancelPremium && (
                        <button
                          onClick={() => setCancelModalOpen(true)}
                          aria-label="Cancel subscription"
                          className="px-2 py-1.5 rounded-lg text-[10px] font-medium text-[var(--text-secondary)] hover:text-[var(--status-red)] hover:bg-[var(--status-red-bg)] transition-colors"
                        >
                          Cancel
                        </button>
                      )}
                    </div>
                  ) : subscriptionType === 'trip_pass' ? (
                    onUpgradeToPremium && (
                      <button
                        onClick={onUpgradeToPremium}
                        aria-label="Subscribe to Pro"
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-gradient-to-r from-[#2dd4bf] to-[#f59e0b] text-black hover:shadow-lg hover:shadow-teal-500/25 transition-all"
                      >
                        <Crown className="w-3 h-3" />
                        {t.settings.subscribe || 'Subscribe'}
                      </button>
                    )
                  ) : onCancelPremium && (
                    <button
                      onClick={() => setCancelModalOpen(true)}
                      aria-label="Cancel subscription"
                      className="px-3 py-1.5 rounded-lg text-xs font-medium text-[var(--text-secondary)] hover:text-[var(--status-red)] hover:bg-[var(--status-red-bg)] transition-colors"
                    >
                      Cancel
                    </button>
                  )}
                </div>
              </div>
              {onPauseSubscription && onCancelPremium && (
                <CancelModal
                  isOpen={cancelModalOpen}
                  onClose={() => setCancelModalOpen(false)}
                  onPause={async (months) => {
                    await onPauseSubscription(months);
                    setCancelModalOpen(false);
                  }}
                  onCancel={async () => {
                    await onCancelPremium();
                    setCancelModalOpen(false);
                  }}
                  subscriptionExpiresAt={subscriptionExpiresAt}
                />
              )}
            </section>
          )}
          </>}

          {/* Tip the Concierge */}
          {isAuthenticated && (onOpenPro || onUpgradeToPremium) && (
            <section>
              <div
                onClick={onOpenPro || onUpgradeToPremium}
                className="p-4 rounded-lg bg-[var(--bg-elevated)] border border-[var(--border)] cursor-pointer hover:border-[var(--accent-teal)]/40 hover:bg-[var(--accent-teal)]/5 hover:shadow-md transition-all"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-[var(--accent-teal)]/10 flex items-center justify-center flex-shrink-0">
                    <Heart className="w-4 h-4 text-[var(--accent-teal)]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-medium text-[var(--text-primary)]">
                      {t.settings.tipConcierge || 'Tip the Concierge'}
                    </h3>
                    <p className="text-xs text-[var(--text-secondary)] mt-0.5">
                      {t.settings.tipConciergeDescription || 'Love TripBoom? A tip helps us build more cool features.'}
                    </p>
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); (onOpenPro || onUpgradeToPremium)?.(); }}
                    aria-label="Tip the concierge"
                    className="flex-shrink-0 px-3 py-1.5 rounded-lg text-xs font-medium text-[var(--accent-teal)] hover:bg-[var(--accent-teal)]/10 border border-[var(--accent-teal)]/30 transition-colors"
                  >
                    {t.settings.tipButton || 'Tip'}
                  </button>
                </div>
              </div>
            </section>
          )}

          <p className="text-xs text-center text-[var(--text-secondary)]/60 pt-2">
            {t.settings.settingsSavedLoggedIn}
          </p>

          <a
            href="https://www.tripboom.app/reviews"
            target="_blank"
            rel="noopener noreferrer"
            className="block text-xs text-center text-[var(--accent-teal)] hover:text-[var(--accent-teal)]/80 pt-3 pb-1 transition-colors"
          >
            {t.settings?.feedbackViaWebsite || 'Feedback via Website ↗'}
          </a>

          {/* H-105c: privacy policy + data rights live on the website.
              The app is post-consent; users manage delete/export/cookies
              from the marketing site. */}
          <a
            href="https://www.tripboom.app/privacy"
            target="_blank"
            rel="noopener noreferrer"
            className="block text-xs text-center text-[var(--text-secondary)] hover:text-[var(--text-primary)] pt-1 pb-1 transition-colors"
          >
            {t.settings?.privacyAndData || 'Privacy & data ↗'}
          </a>

          <a
            href="mailto:support@tripboom.app?subject=TripBoom%20support"
            className="block text-xs text-center text-[var(--text-secondary)] hover:text-[var(--text-primary)] pt-1 pb-1 transition-colors"
          >
            {t.settings.contactSupport}
          </a>

          {isAuthenticated && !isAdmin && deleteStage === 'idle' && (
            <button
              type="button"
              onClick={() => { setDeleteError(null); setDeleteStage('confirm'); }}
              className="block mx-auto text-xs text-center text-[var(--text-secondary)]/80 hover:text-[var(--status-red)] pt-1 pb-1 transition-colors"
            >
              {t.settings.deleteAccount}
            </button>
          )}

          {isAuthenticated && !isAdmin && deleteStage !== 'idle' && (
            <div role="alertdialog" aria-labelledby="delete-account-title" className="mt-2 p-4 rounded-xl border border-[var(--status-red)]/40 bg-[var(--status-red-bg)] space-y-3">
              <p id="delete-account-title" className="text-sm font-semibold text-[var(--text-primary)]">{t.settings.deleteAccountTitle}</p>
              <p className="text-xs text-[var(--text-secondary)] leading-relaxed">{t.settings.deleteAccountBody}</p>
              {deleteError && <p role="alert" className="text-xs text-[var(--status-red)]">{deleteError}</p>}
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setDeleteStage('idle')}
                  disabled={deleteStage === 'deleting'}
                  className="flex-1 py-2.5 rounded-lg border border-[var(--border)] text-sm font-medium text-[var(--text-primary)] disabled:opacity-50"
                >
                  {t.settings.deleteAccountCancel}
                </button>
                <button
                  type="button"
                  onClick={handleDeleteAccount}
                  disabled={deleteStage === 'deleting'}
                  className="flex-1 py-2.5 rounded-lg bg-[var(--status-red)] text-sm font-semibold text-white disabled:opacity-50"
                >
                  {deleteStage === 'deleting' ? t.settings.deleteAccountDeleting : t.settings.deleteAccountConfirm}
                </button>
              </div>
            </div>
          )}

          {isAdmin && (
            <a
              href="/admin"
              className="block text-xs text-center text-[var(--accent-gold)] hover:text-[var(--accent-gold)]/80 pt-2 pb-1 transition-colors"
            >
              Admin Panel ↗
            </a>
          )}
        </div>
      </div>

      {/* H-218: clipboard-fallback toast for the QR / Share Now flow. */}
      {shareCopiedToast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[70] px-4 py-2 rounded-lg bg-[var(--bg-card)] border border-[var(--border)] shadow-lg text-sm text-[var(--text-primary)]">
          {t.share?.copied || 'Copied!'}
        </div>
      )}
    </div>
  );
}

export const DEFAULT_DISTANCE_SETTINGS: DistanceSettings = {
  walking: { '1-2-hrs': 2500, 'half-day': 5000, 'full-day': 10000, '2-3-days': 15000 },
  transit: { '1-2-hrs': 15000, 'half-day': 35000, 'full-day': 50000, '2-3-days': 50000 },
  taxi: { '1-2-hrs': 40000, 'half-day': 50000, 'full-day': 50000, '2-3-days': 50000 }
};

export const DEFAULT_CATEGORY_SETTINGS: CategorySettings = {
  hungry: ['restaurant', 'cafe', 'bakery', 'bar', 'meal_takeaway', 'meal_delivery'],
  'must-sees': ['tourist_attraction', 'museum', 'art_gallery', 'church', 'hindu_temple', 'mosque', 'synagogue', 'park', 'amusement_park', 'aquarium', 'zoo', 'performing_arts_theater'],
  adventurous: ['park', 'national_park', 'campground', 'hiking_area', 'marina', 'amusement_park', 'aquarium', 'zoo', 'sports_club', 'sports_complex', 'stadium', 'ski_resort', 'tourist_attraction', 'athletic_field', 'bowling_alley'],
  'hidden-gems': ['bar', 'cafe', 'book_store', 'art_gallery', 'night_club', 'liquor_store', 'market'],
  popular: ['tourist_attraction', 'restaurant', 'shopping_mall', 'museum', 'park', 'art_gallery'],
  'feeling-lucky': ['night_club', 'casino', 'spa', 'movie_theater', 'bowling_alley', 'amusement_park', 'bar']
};
